<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { baseURL } from '@/services/api.js'
import api from '@/services/api'; 

const scrollText = ref('');
const isMobile = ref(false);
const scrollSpeed = ref(40);  // Default speed for the animation
const marqueeContainer = ref(null); // Ref for the scrolling text container
const animationDuration = ref('40s'); // Default animation duration

const router = useRouter()
const userStore = useUserStore()

// Check if the user is logged in
const isLoggedIn = computed(() => !!userStore.token)

// Computed property to determine if the user is an admin
const isAdminUser = computed(() => {
  const adminTypes = ['webmaster', 'sectoragency'];
  return adminTypes.includes(userStore.user?.user_type || '');
});

// Computed property to determine the dashboard link based on user type
const dashboardLink = computed(() => {
  if (isAdminUser.value) {
    return `${baseURL}admin/`;
  }

  const userType = userStore.user?.user_type;
  switch (userType) {
    case 'localinvestor':
      return '/local-investor/home';
    case 'foreigninvestor':
      return '/foreign-investor/home';
    case 'serviceprovider':
      return '/service-provider/home';
    case 'registeredfdi':
      return '/registered-fdi/home';
    case 'approvedfdi':
      return '/approved-fdi/home';
    case 'rma':
      return '/rma/home';
    case 'directorgeneral':
      return '/director/approved-fdi-visa-application';
    case 'secretary':
      return '/secretariat/approved-fdi-visa-application';
    case 'chief':
      return '/chief/approved-fdi-visa-application';
    case 'investbhutan':
      return '/invest-bhutan/approved-fdi-visa-application';
    default:
      return '/';
  }
});

// Function to handle the dashboard button click
const handleDashboardClick = () => {
  if (isAdminUser.value) {
    window.location.href = dashboardLink.value;
  } else {
    router.push(dashboardLink.value);
  }
};

const fetchMarqueeData = async () => {
  try {
    const response = await api.get('/settings/api/marquee/');
    if (response.status === 200) {
      // Filter active items and concatenate headlines with a spacer between them
      const activeMarquees = response.data
        .filter((item: { is_active: boolean }) => item.is_active)
        .map((item: { headline: string }) => item.headline)
        .join(' | ');
      scrollText.value = activeMarquees;
      
      // Recalculate the speed and animation time
      await nextTick();  // Wait for DOM updates to finish
      calculateAnimationDuration();
    } else {
      console.error('Error fetching marquee data');
    }
  } catch (error) {
    console.error('Error fetching marquee data:', error);
  }
};

// Function to calculate the width of the text and adjust the animation duration dynamically

const calculateAnimationDuration = () => {
  const container = marqueeContainer.value;
  if (container) {
    const containerWidth = (container as HTMLElement).offsetWidth;
    const viewportWidth = window.innerWidth;

    // Set a base speed (pixels per second)
    const baseSpeed = 100;  // Adjust this value as needed (speed in pixels per second)
    
    // Calculate the animation duration based on text width and base speed
    const totalDistance = containerWidth + viewportWidth;
    const durationInSeconds = totalDistance / baseSpeed;
    animationDuration.value = `${durationInSeconds}s`;  // Update the animation duration dynamically
  }
};

// Function to check if the screen size is mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

// Run when the component is mounted
onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  fetchMarqueeData();  // Fetch marquee data on mount
  window.addEventListener('resize', calculateAnimationDuration);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  window.removeEventListener('resize', calculateAnimationDuration);
});

// Logout function
const logout = () => {
  userStore.logout();
  router.push('/');
};

// Languages list and selected language state
const languages = ref<{ code: string; name: string }[]>([]);
const selectedLanguage = ref('en');

// Watch for changes in the languages array and set default language
watch(languages, (newLanguages) => {
  if (newLanguages.length > 0 && !selectedLanguage.value) {
    selectedLanguage.value = newLanguages[0].code;
  }
});

defineExpose({});
</script>

<template>
  <header class="bg-white shadow-md">
    <!-- Marquee section -->
    <div class="bg-gray-100 border-b border-gray-200">
      <div class="container mx-auto">
        <div class="overflow-hidden py-2">
          <div class="whitespace-nowrap animate-scrolling-text text-bhutan-green font-medium">
            {{ scrollText }}
          </div>
        </div>
      </div>
    </div>

    <!-- Main header content -->
    <div class="container mx-auto px-4">
      <div class="flex flex-col md:flex-row items-center py-4 relative">
        <!-- Left: First Logo -->
        <div class="flex-shrink-0 mb-4 md:mb-0 md:mr-8">
          <router-link to="/">
            <img 
              src="@/assets/img/logo.png" 
              alt="RGOB Logo" 
              class="h-15 md:h-20 w-auto"
            />
          </router-link>
        </div>

        <!-- Center: Second Logo (larger and more prominent) -->
        <div class="flex-grow flex justify-center items-center mb-4 md:mb-0">
          <router-link to="/" class="transform md:scale-110">
            <img 
              src="@/assets/img/Picture2.png" 
              alt="Main Logo" 
              class="h-24 md:h-36 w-auto max-w-full object-contain"
            />
          </router-link>
        </div>

        <!-- Right: Action Buttons -->
        <div class="flex-shrink-0 space-y-2 md:space-y-0 md:space-x-3">
          <template v-if="isLoggedIn">
            <button 
              @click="handleDashboardClick"
              class="w-full md:w-auto bg-bhutan-green hover:bg-bhutan-green-light text-white font-bold py-2.5 px-6 rounded-lg transition duration-300 flex items-center justify-center"
            >
              <span v-if="!isMobile">Dashboard</span>
              <span v-else>Dash</span>
            </button>
            <button 
              @click="logout"
              class="w-full md:w-auto bg-bhutan-green hover:bg-bhutan-green-light text-white font-bold py-2.5 px-6 rounded-lg transition duration-300 flex items-center justify-center"
            >
              <span v-if="!isMobile">Logout</span>
              <span v-else>Out</span>
            </button>
          </template>
          <template v-else>
            <router-link to="/login" class="block md:inline-block">
              <button
                class="w-full md:w-auto bg-bhutan-green hover:bg-bhutan-green-light text-white font-bold py-2.5 px-6 rounded-lg transition duration-300 flex items-center justify-center"
              >
                <span v-if="!isMobile">Login</span>
                <span v-else>Log</span>
              </button>
            </router-link>
            <router-link to="/register" class="block md:inline-block">
              <button
                class="w-full md:w-auto bg-bhutan-green hover:bg-bhutan-green-light text-white font-bold py-2.5 px-6 rounded-lg transition duration-300 flex items-center justify-center"
              >
                <span v-if="!isMobile">Register</span>
                <span v-else>Reg</span>
              </button>
            </router-link>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.animate-scrolling-text {
  display: inline-block;
  animation: scrolling-text 40s linear infinite;
}

@keyframes scrolling-text {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* Add smooth transitions for responsive changes */
img {
  transition: all 0.3s ease-in-out;
}

/* Improve button hover states */
button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
